import type { Locale } from "@/core/i18n/locale"

/**
 * Screen text of the free invoice generator in both languages (T1322). Only the form and its messages
 * are translated: the generated invoice document stays an English Tax Invoice (the ATO's wording,
 * `docs/design/landing/copy-zh.md`).
 */
export interface InvoiceGeneratorCopy {
  messages: { seller_name: string; seller_abn: string; lines: string; buyer: string }
  /** Header-field messages, keyed by the English message the model returns. */
  fieldErrors: Record<string, string>
  describeItem: (n: number) => string
  quantityError: (n: number) => string
  amountError: (n: number) => string
  removeItem: (n: number) => string
  yourDetails: string
  businessName: string
  abn: string
  abnPlaceholder: string
  registeredForGst: string
  registeredHint: string
  customerAndInvoice: string
  customerName: string
  customerAbn: string
  invoiceNumber: string
  issueDate: string
  dueDate: string
  items: string
  pricesYouEnter: string
  excludeGst: string
  includeGst: string
  description: string
  quantity: string
  unitPrice: string
  gstFreeItem: string
  remove: string
  addItem: string
  preview: string
  fixThese: string
  downloadPdf: string
  printHint: string
  runsInBrowser: string
  /** Shown only in Chinese: the invoice itself is an English Tax Invoice. */
  englishDocumentNote: string | null
}

const en: InvoiceGeneratorCopy = {
  messages: {
    seller_name: "Add your business name.",
    seller_abn: "Add a valid 11-digit ABN.",
    lines: "Add at least one item.",
    buyer: "A tax invoice of $1,000 or more needs the customer's name or ABN.",
  },
  fieldErrors: {},
  describeItem: (n) => `Describe item ${n}.`,
  quantityError: (n) => `Item ${n}: enter a quantity above 0.`,
  amountError: (n) => `Item ${n}: enter a price, like 450.00.`,
  removeItem: (n) => `Remove item ${n}`,
  yourDetails: "Your details",
  businessName: "Business name",
  abn: "ABN",
  abnPlaceholder: "11 digits",
  registeredForGst: "Registered for GST",
  registeredHint: "Turn on if the ATO has registered you",
  customerAndInvoice: "Customer and invoice",
  customerName: "Customer name",
  customerAbn: "Customer ABN (optional)",
  invoiceNumber: "Invoice number",
  issueDate: "Issue date",
  dueDate: "Due date",
  items: "Items",
  pricesYouEnter: "Prices you enter",
  excludeGst: "Exclude GST",
  includeGst: "Include GST",
  description: "Description",
  quantity: "Quantity",
  unitPrice: "Unit price",
  gstFreeItem: "No GST on this item (GST-free)",
  remove: "Remove",
  addItem: "Add item",
  preview: "Preview",
  fixThese: "Fix these to download your invoice:",
  downloadPdf: "Download PDF",
  printHint: "Opens your print window. Choose Save as PDF as the printer.",
  runsInBrowser: "Runs in your browser. Nothing is sent to us.",
  englishDocumentNote: null,
}

const zh: InvoiceGeneratorCopy = {
  messages: {
    seller_name: "请填写你的业务名称。",
    seller_abn: "请填写有效的 11 位 ABN。",
    lines: "请至少添加一个项目。",
    buyer: "A$1,000 或以上的 Tax Invoice 需要填写客户姓名或 ABN。",
  },
  fieldErrors: {
    "Add an invoice number.": "请填写发票编号。",
    "Choose the issue date.": "请选择开票日期。",
    "Choose the due date.": "请选择到期日。",
    "The due date cannot be before the issue date.": "到期日不能早于开票日期。",
  },
  describeItem: (n) => `请填写第 ${n} 项的说明。`,
  quantityError: (n) => `第 ${n} 项：请填写大于 0 的数量。`,
  amountError: (n) => `第 ${n} 项：请填写单价，例如 450.00。`,
  removeItem: (n) => `删除第 ${n} 项`,
  yourDetails: "你的信息",
  businessName: "业务名称",
  abn: "ABN",
  abnPlaceholder: "11 位数字",
  registeredForGst: "已登记 GST",
  registeredHint: "如果 ATO 已为你登记 GST，请打开",
  customerAndInvoice: "客户和发票",
  customerName: "客户姓名",
  customerAbn: "客户 ABN（选填）",
  invoiceNumber: "发票编号",
  issueDate: "开票日期",
  dueDate: "到期日",
  items: "项目",
  pricesYouEnter: "你填写的价格",
  excludeGst: "不含 GST",
  includeGst: "含 GST",
  description: "说明",
  quantity: "数量",
  unitPrice: "单价",
  gstFreeItem: "此项目不收 GST（GST-free）",
  remove: "删除",
  addItem: "添加项目",
  preview: "预览",
  fixThese: "请先修正以下问题，才能下载发票：",
  downloadPdf: "下载 PDF",
  printHint: "会打开打印窗口。打印机选择“另存为 PDF”。",
  runsInBrowser: "在你的浏览器里运行，不会向我们发送任何内容。",
  englishDocumentNote:
    "生成的发票文件是英文的 Tax Invoice（ATO 规定的措辞），页面上的预览和下载的 PDF 都是英文。表单文字为中文，发票内容请按英文填写。",
}

export const INVOICE_GENERATOR_COPY: Record<Locale, InvoiceGeneratorCopy> = { en, zh }
